import { InvalidCatalogDefinitionError } from "./errors.model";
import { canonicalSegment } from "./lib/canonical";
import { latestRevision } from "./lib/latest-revision";
import type { MemberId } from "./member-identity";
import {
  type QuestionnaireDefinitionState,
  type QuestionnaireMemberMarks,
  type QuestionnaireSection,
  QuestionSetDefinition,
} from "./question-set-definition.model";
import type { AnswerableQuestion } from "./questionnaire-instance.model";
import type { CatalogSpine } from "./spine.model";

// ============================================================
// ConsentFormDefinition — the text a patient agrees to, versioned
//
//   ConsentFormDefinition
//   ├── (spine)
//   ├── body
//   └── sections            what the form asks beyond the signature; often none
//
// A signature pins `url|version`, so neither the text nor the fields are edited
// under one: a change to either is a revision.
// ============================================================

export interface NewConsentFormDefinition {
  spine: CatalogSpine;
  body: string;
  /** Authored in code, so member ids may be given; any left out are assigned. */
  sections?: readonly QuestionnaireSection[];
}

export interface ConsentFormDefinitionFacts extends QuestionnaireDefinitionState {
  body: string;
}

/** Stored revisions may predate their marks; they are raised to clear the members present. */
export type StoredConsentFormDefinitionFacts = Omit<ConsentFormDefinitionFacts, "marks" | "scores" | "totalBands"> & {
  marks?: Partial<QuestionnaireMemberMarks>;
};

/**
 * The core group: every patient signs these, in this order, before the portal
 * opens. Electronic signatures comes first: Texas requires it before anything
 * else is signed electronically (Tex. Bus. & Com. Code 322.005). Any other form
 * is offered after them and gates nothing.
 */
export const CORE_CONSENT_FORMS = [
  "electronic-records-and-signatures",
  "evaluation-and-treatment",
  "privacy-practices",
  "financial-policy",
  "recordings",
  "hyperbaric-oxygen-therapy",
  "patient-rights",
] as const;
export type CoreConsentFormKey = (typeof CORE_CONSENT_FORMS)[number];

export class ConsentFormDefinition extends QuestionSetDefinition<ConsentFormDefinition> {
  private text: string;

  private constructor(
    spine: CatalogSpine,
    body: string,
    sections: readonly QuestionnaireSection[],
    marks: Partial<QuestionnaireMemberMarks> = {},
  ) {
    super(spine, sections, {
      section: marks.section ?? 0,
      item: marks.item ?? 0,
      option: marks.option ?? 0,
      score: marks.score ?? 0,
    });
    this.text = assertBody(body, spine.url);
  }

  static create(definition: NewConsentFormDefinition): ConsentFormDefinition {
    return new ConsentFormDefinition(definition.spine, definition.body, definition.sections ?? []);
  }

  static restore(facts: StoredConsentFormDefinitionFacts): ConsentFormDefinition {
    const { body, sections, marks, ...spine } = facts;
    return new ConsentFormDefinition(spine, body, sections, marks);
  }

  /**
   * The newest revision of each active lineage, the core group first in signing
   * order, then every other form by name. Availability is lineage-level, so an older
   * revision can still read active after its lineage is retired.
   */
  static current(forms: readonly ConsentFormDefinition[]): ConsentFormDefinition[] {
    const lineages = new Map<string, ConsentFormDefinition[]>();
    for (const form of forms) lineages.set(form.url, [...(lineages.get(form.url) ?? []), form]);
    return [...lineages.values()]
      .map((revisions) => latestRevision(revisions) as ConsentFormDefinition)
      .filter((form) => form.isActive)
      .sort((a, b) => a.signingRank - b.signingRank || a.name.localeCompare(b.name));
  }

  get body(): string {
    return this.text;
  }

  get key(): string {
    return canonicalSegment(this.url);
  }

  /** Every question the form asks, wherever it sits, with the group it repeats in. */
  get questions(): readonly AnswerableQuestion[] {
    return this.sections.flatMap((section) =>
      section.items.flatMap((item): AnswerableQuestion[] => {
        if (item.kind === "Question") return [{ id: item.id as MemberId, label: item.label, value: item.value }];
        if (item.kind !== "RepeatingGroup") return [];
        return item.items.flatMap((member) =>
          member.kind === "Question"
            ? [
                {
                  id: member.id as MemberId,
                  label: member.label,
                  value: member.value,
                  repeatingGroupId: item.id as MemberId,
                },
              ]
            : [],
        );
      }),
    );
  }

  get isCore(): boolean {
    return (CORE_CONSENT_FORMS as readonly string[]).includes(this.key);
  }

  private get signingRank(): number {
    const rank = (CORE_CONSENT_FORMS as readonly string[]).indexOf(this.key);
    return rank === -1 ? CORE_CONSENT_FORMS.length : rank;
  }

  protected asksNothingAllowed(): boolean {
    return true;
  }

  snapshot(): ConsentFormDefinitionFacts {
    return this.facts;
  }

  get facts(): ConsentFormDefinitionFacts {
    return { ...super.facts, body: this.text };
  }

  protected copyForRevision(spine: CatalogSpine): ConsentFormDefinition {
    return new ConsentFormDefinition(spine, this.text, this.sections, this.memberMarks);
  }

  protected substantiveFacts(): unknown {
    return { body: this.text, fields: super.substantiveFacts() };
  }
}

function assertBody(body: string, url: string): string {
  if (body.trim() === "") {
    throw new InvalidCatalogDefinitionError("A consent form must have a body", { url });
  }
  return body;
}
